import { useRef, useState } from 'react'
import { Alert, Button, Form, Input } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { signInWithRedirect } from 'aws-amplify/auth'
import { login, processNewEmail, verifyMfa } from '@/features/auth/api'
import { AUTH_PRIMARY_BUTTON_CLASS } from '@/features/auth/constants'
import { clearSession, clearSessionStorage, setSession } from '@/lib/session'
import { buildAuthSession, tokenExpiryFromSeconds } from '@/features/auth/session'
import { resolvePostLoginTarget } from '@/features/auth/returnTo'
import { isAmplifyConfigured } from '@/lib/amplify'
import { authLog } from '@/features/auth/debug'
import { interpretAuthError, interpretLoginResponse, interpretMfaVerifyError } from '@/features/auth/authFlow'
import type { LoginResponse } from '@/features/auth/types'
import MfaCodeStep from './MfaCodeStep'
import {
  clearExtensionIntent,
  postAuthSuccessToExtension,
  readExtensionIntentFromQuery,
  restoreExtensionIntent,
  saveExtensionIntent,
  type ExtensionLoginIntent,
} from '@/features/auth/extensionAuth'

type LoginFormProps = {
  onSuccess: (redirectPath: string) => void
  /** Router-state target from `RequireAuth` or the 401 handler, already checked to be an in-app path. */
  returnTo?: string
  extensionIntent: ExtensionLoginIntent
  updateEmailFlow: {
    enabled: boolean
    id: string | null
  }
}

function postToExtensionIfNeeded(nextSession: { idToken: string; refreshToken: string; tokenExpiry: string }, extensionIntent: ExtensionLoginIntent) {
  const effectiveIntent = extensionIntent.isExtension ? extensionIntent : restoreExtensionIntent()
  if (!effectiveIntent.isExtension) return

  postAuthSuccessToExtension({
    idToken: nextSession.idToken,
    refreshToken: nextSession.refreshToken,
    tokenExpiry: nextSession.tokenExpiry,
  })
  clearExtensionIntent()
}

export default function LoginForm({ onSuccess, returnTo, extensionIntent, updateEmailFlow }: LoginFormProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [xeroLoading, setXeroLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  // Set while the authenticator-code step is showing. The password is kept only for this
  // step (a wrong code is answered with a fresh challenge, and the update-email flow needs it)
  // and is dropped with the state.
  const [mfa, setMfa] = useState<{ email: string; password: string; session: string } | null>(null)
  // Refills the email after "Back to sign in" (the password form remounts).
  const [lastEmail, setLastEmail] = useState<string | undefined>(undefined)
  // Form submit and the boxes' own completion can both fire for the same code.
  const verifyingRef = useRef(false)

  const handleLoginDecision = (decision: ReturnType<typeof interpretLoginResponse>, email: string) => {
    authLog('Login decision resolved', {
      email,
      decision,
    })

    if (decision.kind === 'success') return true

    if (decision.kind === 'verification-sent') {
      setInfo(decision.message)
      return false
    }

    if (decision.kind === 'password-reset-required') {
      navigate('/forgot-password', {
        replace: true,
        state: {
          email,
          info: decision.message,
        },
      })
      return false
    }

    if (decision.kind === 'unconfirmed') {
      setInfo(decision.message)
      return false
    }

    setError(decision.message)
    return false
  }

  const onXeroSignIn = () => {
    authLog('Sign in with Xero clicked')
    if (!isAmplifyConfigured()) {
      authLog('Xero sign-in skipped: Amplify not configured')
      return
    }
    const queryIntent = readExtensionIntentFromQuery()
    if (queryIntent.isExtension) {
      saveExtensionIntent(queryIntent)
    } else if (extensionIntent.isExtension) {
      saveExtensionIntent(extensionIntent)
    }
    setError(null)
    setXeroLoading(true)
    authLog('Starting federatedSignIn with provider Xero')
    signInWithRedirect({ provider: { custom: 'Xero' } }).catch((e: unknown) => {
      authLog('Xero federatedSignIn failed', e)
      setError(e instanceof Error ? e.message : 'Xero sign-in failed')
      setXeroLoading(false)
    })
  }

  const onFinish = async (values: { email: string; password: string }) => {
    setError(null)
    setInfo(null)
    setLoading(true)
    authLog('Login form submit', {
      email: values.email,
      updateEmailFlow,
    })
    try {
      clearSessionStorage()
      const response = await login({
        userName: values.email,
        password: values.password,
        isUpdateEmail: updateEmailFlow.enabled,
        // Only this email update may skip the email link (both sign-ins of the flow).
        updateEmailId: updateEmailFlow.enabled ? updateEmailFlow.id : undefined,
      })
      authLog('confirmationEmail response received', {
        email: values.email,
        response,
      })

      const loginDecision = interpretLoginResponse(response)
      if (loginDecision.kind === 'mfa-required') {
        setMfa({ email: values.email, password: values.password, session: loginDecision.session })
        return
      }

      if (!handleLoginDecision(loginDecision, values.email)) {
        return
      }

      await continueLogin(values.email, values.password, response)
    } catch (e) {
      if (updateEmailFlow.enabled) {
        clearSession()
      }
      authLog('Login flow threw error', e)
      const decision = interpretAuthError(e)
      handleLoginDecision(decision, values.email)
    } finally {
      setLoading(false)
    }
  }

  const onVerifyCode = async (code: string) => {
    if (!mfa || verifyingRef.current) return
    verifyingRef.current = true
    setError(null)
    setLoading(true)
    try {
      let response: LoginResponse
      try {
        response = await verifyMfa({ userName: mfa.email, password: mfa.password, session: mfa.session, code })
      } catch (e) {
        authLog('Authenticator code step threw error', e)
        const failure = interpretMfaVerifyError(e)
        if (failure.kind === 'mfa-retry') {
          setMfa({ ...mfa, session: failure.session })
          setError(failure.message)
        } else if (failure.kind === 'mfa-restart') {
          setMfa(null)
          setError(failure.message)
        } else if (failure.kind === 'locked' || failure.kind === 'inactive') {
          setMfa(null)
          handleLoginDecision(failure, mfa.email)
        } else {
          // Network errors, throttling and the like: the challenge may still be valid, so stay here.
          setError('message' in failure ? failure.message : 'Something went wrong. Please try again.')
        }
        return
      }

      // The code step stays up (loading) while the session is built and the app navigates away.
      try {
        if (!(await continueLogin(mfa.email, mfa.password, response))) setMfa(null)
      } catch (e) {
        if (updateEmailFlow.enabled) {
          clearSession()
        }
        authLog('Login flow threw error after the authenticator code', e)
        setMfa(null)
        handleLoginDecision(interpretAuthError(e), mfa.email)
      }
    } finally {
      verifyingRef.current = false
      setLoading(false)
    }
  }

  const onBackToSignIn = () => {
    setLastEmail(mfa?.email)
    setMfa(null)
    setError(null)
  }

  // Everything after the second factor (or the password, for users without one): the
  // update-email hand-off, the session, the extension hand-off and the redirect.
  // False when it stopped with an error shown instead of navigating.
  const continueLogin = async (email: string, password: string, response: LoginResponse): Promise<boolean> => {
    let sessionEmail = response.email ?? email
    let idToken = response.idToken
    let refreshToken = response.refreshToken
    let expiry = response.expiry

    if (updateEmailFlow.enabled && updateEmailFlow.id) {
      if (!idToken || !refreshToken || expiry == null) {
        setError('Login failed')
        return false
      }

      setSession({
        email: sessionEmail,
        idToken,
        refreshToken,
        tokenExpiry: tokenExpiryFromSeconds(expiry),
        currentClientId: '',
        defaultClientId: '',
      })

      const processed = await processNewEmail({
        id: updateEmailFlow.id,
        password,
      })
      authLog('processNewEmail response received', {
        updateEmailId: updateEmailFlow.id,
        processed,
      })

      if (!processed?.success || !processed.email) {
        clearSession()
        setError('An error occurred during email update process.')
        return false
      }

      sessionEmail = processed.email

      const updatedLoginResponse = await login({
        userName: sessionEmail,
        password,
        isUpdateEmail: true,
        updateEmailId: updateEmailFlow.id,
      })
      authLog('confirmationEmail response received after email update', {
        email: sessionEmail,
        response: updatedLoginResponse,
      })
      const updatedDecision = interpretLoginResponse(updatedLoginResponse)
      // Not expected: the email change recreates the Cognito user without an authenticator, so
      // this second sign-in isn't challenged. If it ever is, the email has already changed, so
      // say that rather than the generic code-step message.
      if (updatedDecision.kind === 'mfa-required') {
        clearSession()
        setError('Your email address was updated. Please sign in again with your new email address.')
        return false
      }
      if (!handleLoginDecision(updatedDecision, sessionEmail)) {
        clearSession()
        return false
      }

      idToken = updatedLoginResponse.idToken
      refreshToken = updatedLoginResponse.refreshToken
      expiry = updatedLoginResponse.expiry
    }

    if (!idToken || !refreshToken || expiry == null) {
      setError('Login failed')
      return false
    }

    const nextSession = await buildAuthSession(
      sessionEmail,
      idToken,
      refreshToken,
      expiry
    )
    setSession(nextSession)
    const activeIntent = extensionIntent.isExtension ? extensionIntent : restoreExtensionIntent()
    postToExtensionIfNeeded(nextSession, activeIntent)
    if (activeIntent.isExtension && activeIntent.redirectUri) {
      window.location.href = activeIntent.redirectUri
      return true
    }
    const redirectPath = await resolvePostLoginTarget(nextSession, returnTo)
    onSuccess(redirectPath)
    return true
  }

  if (mfa) {
    return (
      <MfaCodeStep
        email={mfa.email}
        loading={loading}
        error={error}
        onVerify={(code) => void onVerifyCode(code)}
        onBack={onBackToSignIn}
      />
    )
  }

  return (
    <>
      {error && <Alert className="mb-4 mt-[1.25rem]" type="error" showIcon message={error} />}
      {info && <Alert className="mb-4 mt-[1.25rem]" type="info" showIcon message={info} />}

      <Form layout="vertical" onFinish={onFinish} className="mt-[1rem]" initialValues={{ email: lastEmail }}>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Invalid email' },
          ]}
        >
          <Input autoComplete="username" className="h-[2.625rem]" />
        </Form.Item>
        <Form.Item
          label="Password"
          name="password"
          rules={[{ required: true, message: 'Password is required' }]}
        >
          <Input.Password autoComplete="current-password" className="h-[2.625rem]" />
        </Form.Item>
        <div className="mb-[0.875rem]">
          <Link
            className="text-[0.875rem] text-gray-800 hover:text-gray-900"
            to="/forgot-password"
            state={{ email: '' }}
          >
            Forgot Password
          </Link>
        </div>
        <Button
          htmlType="submit"
          loading={loading}
          className={`${AUTH_PRIMARY_BUTTON_CLASS} w-full`}
        >
          Sign In
        </Button>

        {isAmplifyConfigured() && (
          <Button
            htmlType="button"
            className="mt-[0.75rem] h-[2.75rem] w-full rounded-[0.625rem] border border-[#66cbc7] bg-transparent text-[1rem] text-gray-700 hover:!border-[#58b9b4] hover:!text-gray-800"
            disabled={loading || xeroLoading}
            loading={xeroLoading}
            onClick={onXeroSignIn}
          >
            <span className="flex items-center justify-center gap-2">
              <img src="/assets/media/svg/icons/Xero.svg" alt="" className="h-[1rem] w-[1rem]" />
              <span>Sign in with Xero</span>
            </span>
          </Button>
        )}
      </Form>
    </>
  )
}
